/**
 * BillingDialog
 * ─────────────────────────────────────────────────────────────────────────────
 * Checkout sheet for an existing venue admin to renew or upgrade their plan
 * (Option A — no self-serve account creation). Picks tier / period / currency,
 * confirms an email for the Paystack receipt, shows the server-mirrored total
 * (with the one-time setup fee bundled in until it's been paid), then redirects
 * to Paystack's hosted page.
 *
 * On native (iOS/Android) we never open Paystack — App Store / Play Store rules
 * require IAP for in-app digital subscriptions — so we show a "pay on the web"
 * notice instead.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { usePlatform } from "@/hooks/usePlatform";
import {
  startCheckout, displayTotal, formatMoney, SETUP_FEE_DISPLAY, PLAN_DISPLAY, TIER_LABEL,
  BILLING_ENABLED, type Currency, type Tier, type Period,
} from "@/lib/billing";

interface BillingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  host: any;
  defaultTier?: Tier;
  defaultCurrency?: Currency;
}

const SEGMENT = "flex-1 h-10 rounded-[6px] font-geist text-[13px] font-medium transition-colors";

const BillingDialog = ({ open, onOpenChange, host, defaultTier = "regular", defaultCurrency = "NGN" }: BillingDialogProps) => {
  const { toast } = useToast();
  const { isNative } = usePlatform();

  const [tier, setTier] = useState<Tier>(defaultTier);
  const [period, setPeriod] = useState<Period>("monthly");
  const [currency, setCurrency] = useState<Currency>(defaultCurrency);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Re-sync defaults / email whenever the dialog is (re)opened.
  useEffect(() => {
    if (!open) return;
    setTier(defaultTier);
    setCurrency(defaultCurrency);
    setEmail(host?.email ?? "");
  }, [open, defaultTier, defaultCurrency, host?.email]);

  const setupFeePaid = host?.setup_fee_paid === true;
  const total = displayTotal(currency, tier, period, !setupFeePaid);

  const handlePay = async () => {
    if (!BILLING_ENABLED) {
      toast({ title: "Online billing coming soon", description: "To renew or upgrade now, contact info@nairoke.com.", variant: "destructive" });
      return;
    }
    const sessionToken = localStorage.getItem("hostSessionToken");
    if (!sessionToken) {
      toast({ title: "Not signed in", description: "Please sign in again.", variant: "destructive" });
      return;
    }
    const trimmed = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast({ title: "Email required", description: "Enter a valid email for your receipt.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { authorization_url } = await startCheckout({ tier, period, currency, email: trimmed });
      // Hand off to Paystack's hosted checkout.
      window.location.href = authorization_url;
    } catch (err: any) {
      console.error("[billing] checkout failed:", err);
      toast({ title: "Checkout failed", description: err?.message ?? "Please try again.", variant: "destructive" });
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-console-surface border-console-line text-console-text">
        <DialogHeader>
          <DialogTitle className="font-geist tracking-tight">Manage your plan</DialogTitle>
          <DialogDescription className="font-geist text-console-muted">
            {setupFeePaid
              ? "Renew or change your plan. Charges extend your current expiry date."
              : "Your first payment includes the one-time setup fee."}
          </DialogDescription>
        </DialogHeader>

        {isNative ? (
          <div className="rounded-[8px] border border-console-line bg-console-bg/40 p-4 space-y-2">
            <p className="font-geist text-[14px] text-console-text font-medium">Pay on the web</p>
            <p className="font-geist text-[13px] text-console-muted leading-relaxed">
              Subscriptions are managed on our website. Open{" "}
              <span className="text-console-primary">nairoke.com</span> in a browser, sign in, and
              renew or upgrade there. Your plan updates here automatically.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tier */}
            <div className="space-y-1.5">
              <Label className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Plan</Label>
              <div className="flex gap-2">
                {(["regular", "enterprise"] as Tier[]).map((t) => (
                  <button key={t} type="button" onClick={() => setTier(t)}
                    className={`${SEGMENT} ${tier === t ? "bg-console-primary text-console-bg" : "bg-white/5 text-console-muted hover:bg-white/10"}`}>
                    {TIER_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>

            {/* Period */}
            <div className="space-y-1.5">
              <Label className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Billing</Label>
              <div className="flex gap-2">
                {(["monthly", "annual"] as Period[]).map((p) => (
                  <button key={p} type="button" onClick={() => setPeriod(p)}
                    className={`${SEGMENT} ${period === p ? "bg-console-primary text-console-bg" : "bg-white/5 text-console-muted hover:bg-white/10"}`}>
                    {p === "monthly" ? "Monthly" : "Annual"}
                  </button>
                ))}
              </div>
            </div>

            {/* Currency */}
            <div className="space-y-1.5">
              <Label className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Currency</Label>
              <div className="flex gap-2">
                {(["NGN", "USD"] as Currency[]).map((c) => (
                  <button key={c} type="button" onClick={() => setCurrency(c)}
                    className={`${SEGMENT} ${currency === c ? "bg-console-primary text-console-bg" : "bg-white/5 text-console-muted hover:bg-white/10"}`}>
                    {c === "NGN" ? "Naira (₦)" : "USD ($)"}
                  </button>
                ))}
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="billing-email" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Receipt email</Label>
              <Input id="billing-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@venue.com"
                className="h-11 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim rounded-[6px] focus:border-console-primary focus-visible:ring-0" />
            </div>

            {/* Total breakdown */}
            <div className="rounded-[8px] border border-console-line bg-console-bg/40 p-3 space-y-1.5">
              <div className="flex justify-between font-geist text-[13px]">
                <span className="text-console-muted">{TIER_LABEL[tier]} · {period === "monthly" ? "1 month" : "12 months"}</span>
                <span className="text-console-text">{formatMoney(currency, PLAN_DISPLAY[currency][tier][period])}</span>
              </div>
              {!setupFeePaid && (
                <div className="flex justify-between font-geist text-[13px]">
                  <span className="text-console-muted">One-time setup fee</span>
                  <span className="text-console-text">{formatMoney(currency, SETUP_FEE_DISPLAY[currency])}</span>
                </div>
              )}
              <div className="h-px bg-console-line my-1" />
              <div className="flex justify-between font-geist text-[15px] font-semibold">
                <span>Total today</span>
                <span>{formatMoney(currency, total)}</span>
              </div>
            </div>

            <Button onClick={handlePay} disabled={loading || !BILLING_ENABLED}
              className="w-full h-12 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]">
              {!BILLING_ENABLED ? "Online billing coming soon" : loading ? "Redirecting to Paystack…" : `Pay ${formatMoney(currency, total)}`}
            </Button>
            <p className="font-geist-mono text-[10px] text-console-dim text-center">
              {!BILLING_ENABLED
                ? "Online checkout isn't available yet. To renew or upgrade, contact info@nairoke.com."
                : "Secure checkout via Paystack. You'll be redirected to complete payment."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BillingDialog;
